/**
 * Shared click policy for in-app navigation rendered as a real `<a href>` (LSP-20455).
 *
 * Interactive rows used to be plain `<div @click>` / `<button @click>` elements that
 * called `router.push`, which broke every browser affordance a user expects from something that
 * navigates: Ctrl/Cmd-click, middle-click, and the right-click "Open link in new tab" menu all did
 * nothing. Rendering an anchor restores those for free — the only thing left is deciding, on click,
 * whether the SPA router should take over or the browser should be left alone.
 *
 * This mirrors vue-router's own `guardEvent`. RouterLink's custom slot would give the same behavior,
 * but it needs a fully installed router instance, which the component test harness does not provide
 * (it supplies a mock via `provide`), so the policy is expressed directly here instead.
 */
export function shouldLetBrowserHandleClick(event: MouseEvent): boolean {
	// A modifier key means the user asked for a new tab/window/download — never hijack that.
	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return true;
	// Anything but the primary button (middle-click opens a tab, right-click opens the menu).
	// `button` is 0 for synthetic events that omit it, which is the correct default.
	if (event.button !== undefined && event.button !== 0) return true;
	// Something upstream already handled this click.
	if (event.defaultPrevented) return true;
	return false;
}
